import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {OpenInNew} from "@mui/icons-material";
import {SvgIcon, Tooltip} from "@mui/material";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../../../../../packages/data-grid";
import {VendorPurchaseOrdersDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {ReactComponent as Printer} from "../../../../../../../../../assets/images/printer.svg";

/**
 * Purchase orders tab PartnerDataGrid section.
 */
const PurchaseOrdersListOrdersSection = ({
                                             data,
                                             searchData,
                                             dataGridSaveConfig,
                                             navigateToOrder,
                                             defaultVendor,
                                             getExportInfo,
                                         }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.ordersList'});
    const operatorAccessLevels = useAccessLevels();
    const canPrint = operatorAccessLevels.packages.bizPrint.printPurchaseOrder;

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            name: VendorPurchaseOrdersDataGridCellKeys.navigate,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            width: 0,
            pinned: true,
        },
        ...(canPrint ? [{
            name: VendorPurchaseOrdersDataGridCellKeys.print,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            width: 0,
            pinned: true,
            pinnedToggleable: false,
            resizable: false,
            visibilityToggleable: false,
        }] : []),
        {
            title: t('columns.orderNo'),
            name: VendorPurchaseOrdersDataGridCellKeys.orderNo,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.date'),
            name: VendorPurchaseOrdersDataGridCellKeys.date,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.status'),
            name: VendorPurchaseOrdersDataGridCellKeys.status,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.total'),
            name: VendorPurchaseOrdersDataGridCellKeys.total,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.right,
        },
        {
            title: t('columns.currency'),
            name: VendorPurchaseOrdersDataGridCellKeys.currency,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        ...(!defaultVendor?.id ? [{
            title: t('columns.vendor'),
            name: VendorPurchaseOrdersDataGridCellKeys.vendor,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        }] : []),
    ]), [canPrint, defaultVendor?.id, t]);

    const [dataGridColumns, , , density] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const rows = useMemo(() => data.map((row) => {
        const vendorId = defaultVendor?.id ?? row.vendor?.id ?? row.vendorId;
        const open = () => navigateToOrder(row, vendorId);
        return {
            key: row.id,
            data: row,
            cells: {
                [VendorPurchaseOrdersDataGridCellKeys.navigate]: (
                    defaultVendor?.id && vendorId
                        ? (
                            <IconButton
                                color={'secondary'}
                                component={Link}
                                to={routeFunctions.vendor.purchases.orders.single(vendorId, row.id)}
                            >
                                <OpenInNew className={'icon normal'}/>
                            </IconButton>
                        )
                        : (
                            <IconButton color={'secondary'} onPressed={open}>
                                <OpenInNew className={'icon normal'}/>
                            </IconButton>
                        )
                ),
                ...(canPrint && vendorId ? {
                    [VendorPurchaseOrdersDataGridCellKeys.print]: (
                        <Tooltip arrow title={t('actions.print')}>
                            <IconButton
                                color={'secondary'}
                                component={Link}
                                target={'_blank'}
                                to={routeFunctions.print.purchaseOrder(vendorId, row.id)}
                                aria-label={t('actions.print')}
                            >
                                <SvgIcon className={'icon normal'} component={Printer}/>
                            </IconButton>
                        </Tooltip>
                    ),
                } : {}),
                [VendorPurchaseOrdersDataGridCellKeys.orderNo]: row.orderNo ?? row.purchaseNo,
                [VendorPurchaseOrdersDataGridCellKeys.date]: row.date ?? row.submittedDateTime,
                [VendorPurchaseOrdersDataGridCellKeys.status]: row.status ?? row.statusTitle,
                [VendorPurchaseOrdersDataGridCellKeys.total]: formatMoney(row.total),
                [VendorPurchaseOrdersDataGridCellKeys.currency]: row.currency ?? 'CAD',
                [VendorPurchaseOrdersDataGridCellKeys.vendor]:
                    row.vendor?.name ?? row.vendor?.title ?? '',
            },
        };
    }), [canPrint, data, defaultVendor?.id, navigateToOrder, t]);

    return (
        <PartnerDataGrid
            {...dataGridSaveConfig}
            initialLayoutState={{columns}}
            columns={dataGridColumns}
            rows={rows}
            hideFooterActions
            getExportInfo={getExportInfo}
            state={{
                pagination: searchData.paginationInfo,
                sortBy: searchData.orderBy,
                loading: {state: searchData.loading},
                density,
            }}
            onSortByChanged={(_, current) => searchData.setOrderBy(current)}
            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
            onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
        />
    );
};

export default PurchaseOrdersListOrdersSection;
